import moment from "moment";

// Customer refill follow-ups: dates, the WhatsApp message and its link

export const FOLLOW_UP_STATUS = { pending: "pending", done: "done" };
export const FOLLOW_UP_DATE_FORMAT = "YYYY-MM-DD";
const DISPLAY_DATE_FORMAT = "DD MMM YYYY";
const DEFAULT_COUNTRY_CODE = "91";
const LOCAL_PHONE_LENGTH = 10;
export const FOLLOW_UP_MESSAGE_MAX_LENGTH = 2000;
export const FOLLOW_UP_COMMENT_MAX_LENGTH = 1000;

// Same as the backend's seeded template (migration_refill_followup.ts)
export const DEFAULT_FOLLOW_UP_MESSAGE = `Hello {customer_name},

This is a friendly reminder from {store_name}. The following medicines are due for refill on {refill_date}:
{items}

Reply to this message or visit us and we will keep them ready for you.

Thank you!`;

// Placeholders a message template can use, with the sample the preview shows
export const MESSAGE_PLACEHOLDERS = [
  { key: "customer_name", label: "Customer name", sample: "Ravi Kumar" },
  { key: "phone", label: "Phone number", sample: "9876543210" },
  { key: "store_name", label: "Store name", sample: "City Pharmacy" },
  {
    key: "items",
    label: "Items to refill",
    sample: "• Dolo 650 — Qty 15\n• Pan 40 — Qty 10",
  },
  { key: "refill_date", label: "Refill date", sample: "12 Nov 2026" },
  { key: "bill_number", label: "Bill number", sample: "IN-NEX-3692" },
];

export const SAMPLE_MESSAGE_VALUES = Object.fromEntries(
  MESSAGE_PLACEHOLDERS.map(({ key, sample }) => [key, sample])
);

// Due filters: the refill date range each one asks the list for
export const DUE_FILTERS = [
  { key: "all", label: "Any date" },
  { key: "overdue", label: "Overdue" },
  { key: "today", label: "Today" },
  { key: "week", label: "Next 7 days" },
  { key: "custom", label: "Custom" },
];

export const todayDate = () => moment().format(FOLLOW_UP_DATE_FORMAT);

export const getDueRange = (dueKey, custom = {}) => {
  const today = moment();
  const todayText = today.format(FOLLOW_UP_DATE_FORMAT);
  switch (dueKey) {
    case "overdue":
      return { toDate: today.clone().subtract(1, "day").format(FOLLOW_UP_DATE_FORMAT) };
    case "today":
      return { fromDate: todayText, toDate: todayText };
    // after today, like the summary's "Next 7 days" count
    case "week":
      return {
        fromDate: today.clone().add(1, "day").format(FOLLOW_UP_DATE_FORMAT),
        toDate: today.clone().add(7, "days").format(FOLLOW_UP_DATE_FORMAT),
      };
    case "custom":
      return { fromDate: custom.fromDate || undefined, toDate: custom.toDate || undefined };
    default:
      return {};
  }
};

export const formatDisplayDate = (date) =>
  date ? moment(date, FOLLOW_UP_DATE_FORMAT).format(DISPLAY_DATE_FORMAT) : "--";

export const formatDateTime = (dateTime) =>
  dateTime ? moment(dateTime).format("DD MMM YYYY, hh:mm A") : "";

export const formatQty = (value) => {
  const qty = parseFloat(value) || 0;
  return Number.isInteger(qty) ? String(qty) : String(Math.round(qty * 100) / 100);
};

// Where a follow-up stands against today: done, overdue, due today or upcoming
export const getDueState = (refillDate, status) => {
  if (status === FOLLOW_UP_STATUS.done) return { key: "done", label: "Completed" };
  const days = moment(refillDate, FOLLOW_UP_DATE_FORMAT)
    .startOf("day")
    .diff(moment().startOf("day"), "days");
  if (days < 0) {
    return { key: "overdue", label: `Overdue by ${-days} day${days === -1 ? "" : "s"}` };
  }
  if (days === 0) return { key: "today", label: "Due today" };
  return { key: "upcoming", label: days === 1 ? "Due tomorrow" : `Due in ${days} days` };
};

export const getCustomerName = (followUp) =>
  followUp?.customer_name?.trim() || "Walk-in customer";

export const getInitials = (name) =>
  String(name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

// Fills {placeholder}s in a template; unknown ones are left as typed
export const fillTemplate = (template, values) =>
  String(template ?? "").replace(/\{(\w+)\}/g, (match, key) =>
    values?.[key] === undefined || values?.[key] === null ? match : String(values[key])
  );

// One line per item for {items}. When the items fall due on different days
// each line says its own date, since {refill_date} is the earliest one.
export const formatItemsList = (items) => {
  const hasSeveralDates = new Set(items.map((item) => item?.item_refill_date)).size > 1;
  return items
    .map((item) => {
      const dueText = hasSeveralDates ? ` (due ${formatDisplayDate(item?.item_refill_date)})` : "";
      return `• ${item?.item_name ?? "Item"} — Qty ${formatQty(item?.item_quantity)}${dueText}`;
    })
    .join("\n");
};

// The WhatsApp message for a follow-up, listing the items picked to send
export const buildFollowUpMessage = ({ template, followUp, items, storeName }) => {
  const [earliestDate] = items
    .map((item) => item?.item_refill_date)
    .filter(Boolean)
    .sort();
  return fillTemplate(template || DEFAULT_FOLLOW_UP_MESSAGE, {
    customer_name: followUp?.customer_name?.trim() || "Customer",
    phone: followUp?.cmr_phone_number ?? "",
    store_name: storeName || "our store",
    items: formatItemsList(items),
    refill_date: formatDisplayDate(earliestDate ?? followUp?.item_refill_date),
    bill_number: followUp?.sot_invoice_number ?? "",
  });
};

// WhatsApp needs the country code; a 10 digit (Indian) number gets 91
export const toWhatsAppNumber = (phone) => {
  const digits = String(phone ?? "").replace(/\D/g, "");
  if (digits.length === LOCAL_PHONE_LENGTH) return `${DEFAULT_COUNTRY_CODE}${digits}`;
  return digits.length > LOCAL_PHONE_LENGTH ? digits : "";
};

export const hasCallablePhone = (phone) => toWhatsAppNumber(phone) !== "";

// wa.me opens the WhatsApp app when it is installed, WhatsApp Web otherwise
export const buildWhatsAppUrl = (phone, message) => {
  const number = toWhatsAppNumber(phone);
  return number ? `https://wa.me/${number}?text=${encodeURIComponent(message ?? "")}` : "";
};

// Inside the desktop (Electron) app a link would open in an app window, so
// hand it to the OS; in a browser it opens a new tab
export const openExternalUrl = (url) => {
  const ipc = window.electron?.ipcRenderer;
  if (ipc) {
    ipc.send("open-external", url);
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
};
